import * as THREE from 'three';

/**
 * ReflectBarrier creates a shimmering mirror-like barrier for 0.5s.
 * If a projectile hits during the window, it reverses direction at 80% damage.
 * Visual: mirror plane with iridescent shimmer, flashes on successful reflect, shatters if nothing reflects.
 */
export class ReflectBarrier {
  private scene: THREE.Scene;
  private mesh: THREE.Group;
  private mirrorPlane!: THREE.Mesh;
  private shimmerParticles!: THREE.Points;
  private isActive: boolean = true;
  private duration: number;
  private startTime: number;
  private damageReturn: number;
  private hasReflected: boolean = false;
  private disposed: boolean = false;
  private animationId: number | null = null;
  private flashAnimationId: number | null = null;

  constructor(scene: THREE.Scene, position: THREE.Vector3, duration: number, damageReturn: number) {
    this.scene = scene;
    this.duration = duration;
    this.damageReturn = damageReturn;
    this.startTime = Date.now();

    this.mesh = new THREE.Group();
    this.mesh.position.copy(position);

    this.createMirrorPlane();
    this.createShimmerParticles();

    this.scene.add(this.mesh);
  }

  private createMirrorPlane(): void {
    // Flat mirror-like barrier facing forward
    const geometry = new THREE.PlaneGeometry(3, 3, 8, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0x88CCFF,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });
    this.mirrorPlane = new THREE.Mesh(geometry, material);
    this.mirrorPlane.position.y = 1.5;
    this.mesh.add(this.mirrorPlane);

    // Wireframe overlay for hex grid effect
    const wireGeometry = new THREE.PlaneGeometry(3.1, 3.1, 6, 6);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: 0xAADDFF,
      transparent: true,
      opacity: 0.6,
      wireframe: true,
      side: THREE.DoubleSide,
    });
    const wireframe = new THREE.Mesh(wireGeometry, wireMaterial);
    wireframe.position.y = 1.5;
    this.mesh.add(wireframe);

    // Edge glow ring
    const ringGeometry = new THREE.RingGeometry(1.4, 1.6, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xCCEEFF,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.y = 1.5;
    this.mesh.add(ring);

    // Add point light for mirror glow
    const light = new THREE.PointLight(0x88CCFF, 2, 8);
    light.position.y = 1.5;
    this.mesh.add(light);
  }

  private createShimmerParticles(): void {
    const count = 40;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      // Distribute along the plane surface
      positions[i * 3] = (Math.random() - 0.5) * 3;
      positions[i * 3 + 1] = 0.5 + Math.random() * 2.5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.3;

      // Iridescent colors: cycle through cyan, blue, white
      const t = Math.random();
      if (t < 0.33) {
        colors[i * 3] = 0.6; colors[i * 3 + 1] = 0.9; colors[i * 3 + 2] = 1;
      } else if (t < 0.66) {
        colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.9; colors[i * 3 + 2] = 1;
      } else {
        colors[i * 3] = 0.5; colors[i * 3 + 1] = 0.7; colors[i * 3 + 2] = 1;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.12,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
    });

    this.shimmerParticles = new THREE.Points(geometry, material);
    this.mesh.add(this.shimmerParticles);
  }

  update(_delta: number): boolean {
    if (!this.isActive || this.disposed) return false;

    const elapsed = Date.now() - this.startTime;
    if (elapsed >= this.duration) {
      this.deactivate();
      return false;
    }

    // Shimmer animation - particles drift along the plane
    if (this.shimmerParticles) {
      const positions = this.shimmerParticles.geometry.attributes.position.array as Float32Array;
      const count = positions.length / 3;
      const time = Date.now() * 0.003;

      for (let i = 0; i < count; i++) {
        positions[i * 3] += Math.sin(time + i * 0.5) * 0.01;
        positions[i * 3 + 1] += Math.cos(time + i * 0.3) * 0.008;

        // Keep within bounds
        if (Math.abs(positions[i * 3]) > 1.6) positions[i * 3] *= 0.9;
        if (positions[i * 3 + 1] < 0.3 || positions[i * 3 + 1] > 3.0) {
          positions[i * 3 + 1] = 0.5 + Math.random() * 2.5;
        }
      }
      this.shimmerParticles.geometry.attributes.position.needsUpdate = true;
    }

    // Pulse the mirror plane
    if (this.mirrorPlane) {
      const pulse = 1 + Math.sin(Date.now() * 0.01) * 0.05;
      this.mirrorPlane.scale.set(pulse, pulse, 1);
      const mat = this.mirrorPlane.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.3 + Math.sin(Date.now() * 0.008) * 0.15;
    }

    return true;
  }

  /** Called when a projectile hits this barrier. Returns the damage to send back. */
  reflectProjectile(incomingDamage: number): number {
    if (!this.isActive) return 0;

    this.hasReflected = true;
    this.createReflectFlash();
    return Math.round(incomingDamage * this.damageReturn);
  }

  private createReflectFlash(): void {
    // Bright flash on the barrier surface
    const flashGeometry = new THREE.SphereGeometry(2, 16, 16);
    const flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xFFFFFF,
      transparent: true,
      opacity: 0.9,
    });
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.copy(this.mesh.position);
    flash.position.y = 1.5;
    this.scene.add(flash);

    // Flash light
    const light = new THREE.PointLight(0xCCEEFF, 5, 15);
    light.position.copy(flash.position);
    this.scene.add(light);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;
      flashMaterial.opacity = 0.9 * Math.max(0, 1 - elapsed / 0.3);
      flash.scale.setScalar(1 + elapsed * 4);
      light.intensity = 5 * Math.max(0, 1 - elapsed / 0.25);

      if (elapsed < 0.3) {
        this.flashAnimationId = requestAnimationFrame(animate);
      } else {
        this.flashAnimationId = null;
        this.scene.remove(flash);
        this.scene.remove(light);
        flashGeometry.dispose();
        flashMaterial.dispose();
        light.dispose();
      }
    };
    this.flashAnimationId = requestAnimationFrame(animate);
  }

  private deactivate(): void {
    this.isActive = false;
    if (!this.hasReflected) {
      this.createShatterEffect();
    }
    this.dispose();
  }

  private createShatterEffect(): void {
    // Barrier shatters into glass-like fragments
    const shardCount = 30;
    const positions = new Float32Array(shardCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < shardCount; i++) {
      positions[i * 3] = this.mesh.position.x + (Math.random() - 0.5) * 3;
      positions[i * 3 + 1] = this.mesh.position.y + 0.5 + Math.random() * 2.5;
      positions[i * 3 + 2] = this.mesh.position.z + (Math.random() - 0.5) * 0.5;

      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 3,
        Math.random() * 2 - 0.5,
        (Math.random() - 0.5) * 3,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0x88CCFF,
      size: 0.15,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
    });

    const shatter = new THREE.Points(geometry, material);
    this.scene.add(shatter);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;
      const pos = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < shardCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 5 * 0.016;
      }
      geometry.attributes.position.needsUpdate = true;
      material.opacity = 0.8 * Math.max(0, 1 - elapsed / 0.6);

      if (elapsed < 0.6) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
        this.scene.remove(shatter);
        geometry.dispose();
        material.dispose();
      }
    };
    this.animationId = requestAnimationFrame(animate);
  }

  isBarrierActive(): boolean {
    return this.isActive;
  }

  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  getDamageReturn(): number {
    return this.damageReturn;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.flashAnimationId !== null) {
      cancelAnimationFrame(this.flashAnimationId);
      this.flashAnimationId = null;
    }

    this.scene.remove(this.mesh);
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Light) {
        child.dispose?.();
      }
    });
  }
}
